import GermanDateInput from "@/components/GermanDateInput";
import { InfoTip } from "@/components/InfoTip";
import AvgsClientCalendar from "@/components/AvgsClientCalendar";
import { ClientChatPanel } from "@/components/ClientChatPanel";
import { useChatUnread } from "@/lib/useChatUnread";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, Download, Sparkles, Save, Trash2, Plus, Pencil, FileText, FileClock, ClipboardSignature, CalendarClock, AlertTriangle, Mail, Upload, Lock, Star, Phone, LayoutDashboard, Briefcase, Target, MessageSquare, MessageCircle, PartyPopper, Link2 } from "lucide-react";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import UnicornCelebration from "@/components/UnicornCelebration";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDeShort, formatDeShortDateTime } from "@/lib/date";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import SectionDeliverablesPanel from "@/components/SectionDeliverablesPanel";
import JobApplicationsPanel from "@/components/JobApplicationsPanel";
import { ScheduleSuggestButton } from "@/components/ScheduleSuggest";
import { TraegerStatusCard } from "@/components/TraegerStatusCard";
import { ClientCertificates } from "@/components/ClientCertificates";
import { HummingbirdIcon } from "@/components/Hummingbird";
import { CoachingDokuCard } from "@/components/CoachingDokuCard";
import ClientGoalsPanel from "@/components/ClientGoalsPanel";
import AppointmentDialog from "@/components/AppointmentDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CvProfileReadOnly from "@/components/CvProfileReadOnly";
import EditableUeCell from "@/components/EditableUeCell";
import OnboardingAssignEditor from "@/components/OnboardingAssignEditor";
import AvgsFeedbackPanel from "@/components/AvgsFeedbackPanel";
import ClientDocumentsPanel from "@/components/ClientDocumentsPanel";
import ClientBrandingPanel from "@/components/ClientBrandingPanel";
import ContractUeWarning from "@/components/ContractUeWarning";
import ComplaintFormButton from "@/components/ComplaintFormButton";
import QuestionnaireCard from "@/components/QuestionnaireCard";
import PlannerHistoryCard from "@/components/PlannerHistoryCard";
import AppointmentOutcomeCard from "@/components/AppointmentOutcomeCard";

const TAB_GROUPS = [
  { k: "overview", l: "Übersicht", i: LayoutDashboard, subs: [{ k: "overview", l: "Übersicht" }] },
  { k: "termine", l: "Termine & Kalender", i: CalendarClock, subs: [{ k: "termine", l: "Termine" }, { k: "kalender", l: "Kalender" }] },
  { k: "chat", l: "Chat", i: MessageCircle, subs: [{ k: "chat", l: "Chat" }] },
  { k: "bewerbungen", l: "Bewerbung & Ziele", i: Briefcase, subs: [{ k: "bewerbungen", l: "Bewerbung" }, { k: "ziele", l: "Zielvereinbarung" }] },
  { k: "doc", l: "Dokumentation & Berichte", i: FileText, subs: [{ k: "doc", l: "Tagesdokumentation" }, { k: "interim_report", l: "Zwischenbericht" }, { k: "report", l: "Abschlussbericht" }] },
  { k: "unterlagen", l: "Unterlagen", i: ClipboardSignature, subs: [{ k: "unterlagen", l: "Teilnehmerunterlagen" }] },
  { k: "branding", l: "Branding-Guide", i: Sparkles, subs: [{ k: "branding", l: "Selbstvermarktung" }] },
];
const todayIso = () => new Date().toISOString().slice(0, 10);
const DAY_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const fmtDate = (iso) => {
  try { const d = new Date(iso + "T00:00:00"); return `${DAY_DE[(d.getDay() + 6) % 7]}, ${formatDeShort(iso)}`; } catch { return iso; }
};

export default function AvgsClientDetailPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const canManageClients = user?.role === "admin" || user?.role === "manager" || (user?.all_roles || []).includes("manager") || (user?.all_roles || []).includes("admin");
  const [client, setClient] = useState(null);
  const canDocCheck = (user?.all_roles || [user?.role]).includes("admin") || !!user?.can_doc_check;
  const [searchParams] = useSearchParams();
  const chatUnread = useChatUnread(true).by_client[clientId] || 0;
  const TAB_ALIAS = { traeger: "unterlagen", ergebnisse: "overview", cv: "bewerbungen", invoice: "overview" };
  const [docsKey, setDocsKey] = useState(0);
  const [tab, setTab] = useState(TAB_ALIAS[searchParams.get("tab")] || searchParams.get("tab") || "overview");
  useEffect(() => {
    if (!searchParams.get("tab")) return;
    const gk = (TAB_GROUPS.find((g) => g.subs.some((s) => s.k === tab)) || TAB_GROUPS[0]).k;
    const go = () => document.querySelector(`[data-testid="tab-${gk}"]`)?.scrollIntoView({ block: "nearest", inline: "center" });
    const t1 = setTimeout(go, 600), t2 = setTimeout(go, 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line
  // overview | ergebnisse | bewerbungen | ziele | termine | attendance | doc | report
  const [attendances, setAttendances] = useState([]);
  const [docs, setDocs] = useState([]);
  const [report, setReport] = useState(null);
  const [interimReport, setInterimReport] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [ueSummary, setUeSummary] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myPhase, setMyPhase] = useState(null);
  const [migratingPricing, setMigratingPricing] = useState(false);

  const migrateToCarveout = async () => {
    setMigratingPricing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/migrate-to-carveout-pricing`);
      toast.success(`Umgestellt — Gesamt-Kontingent jetzt ${data.total_ue} UE (Onboarding/Offboarding kommen ab sofort daraus, nicht mehr zusätzlich).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setMigratingPricing(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, d, appts, ue, cs] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/attendance`, { params: { client_id: clientId } }),
        api.get(`/dailydocs`, { params: { client_id: clientId } }),
        api.get(`/appointments`, { params: { client_id: clientId } }),
        api.get(`/clients/${clientId}/ue-summary`),
        api.get(`/coaches`),
      ]);
      setClient(c.data);
      setAttendances(a.data);
      setDocs(d.data);
      setAppointments(appts.data);
      setUeSummary(ue.data);
      setCoaches(cs.data);
      try {
        const r = await api.get(`/clients/${clientId}/final-report`);
        setReport(r.data);
      } catch { setReport(null); }
      try {
        const ir = await api.get(`/clients/${clientId}/interim-report`);
        setInterimReport(ir.data);
      } catch { setInterimReport(null); }
      try {
        const mp = await api.get("/my-phases");
        setMyPhase((mp.data.phases || []).find((p) => p.client_id === clientId) || null);
      } catch { setMyPhase(null); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const apptCounts = useMemo(() => {
    const counts = { proposed: 0, accepted: 0, completed: 0, signed: 0, rejected: 0 };
    for (const a of appointments) counts[a.status] = (counts[a.status] || 0) + 1;
    return counts;
  }, [appointments]);

  const assignedCoachNames = useMemo(() => {
    const ids = client?.coach_user_ids || [];
    const byId = { ...(client?.staff_names || {}), ...Object.fromEntries(coaches.map((c) => [c.user_id, c.name])) };
    return ids.map((id) => byId[id] || id).join(", ") || "—";
  }, [client, coaches]);

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const u = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = u; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(u);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="p-12 text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>;
  if (!client) return <div className="p-12 text-sm text-slate-600">Kunde nicht gefunden.</div>;

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="avgs-detail-root">
      <Link to="/avgs" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-overline text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft size={12} strokeWidth={1.5} /> Alle Kunden
      </Link>
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
          Kd-Nr {client.customer_number || "—"} · Maßn. {client.measure_number || "—"}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">{client.name}</h1>
        <div className="text-sm text-slate-600 font-mono mt-2 flex items-center gap-2">
          <Calendar size={12} strokeWidth={1.5} />{formatDeShort(client.start_date)} – {formatDeShort(client.end_date)}
          {client.archived && <span className="ml-2 text-[10px] font-sans font-bold uppercase tracking-overline bg-slate-200 text-slate-700 px-2 py-0.5" data-testid="client-archived-badge">Archiviert</span>}
          {canManageClients && (
            <button onClick={async () => {
              if (!client.archived && !window.confirm("Kunde archivieren? Er verschwindet aus den Übersichten, die Akte bleibt erreichbar.")) return;
              try { await api.put(`/clients/${clientId}/archived`, { archived: !client.archived }); toast.success(client.archived ? "Kunde wiederhergestellt" : "Kunde archiviert"); load(); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
            }} className="ml-3 font-sans text-[11px] border border-slate-300 px-2 py-0.5 hover:border-slate-900 transition-colors" data-testid="client-archive-toggle">
              {client.archived ? "Wiederherstellen" : "Archivieren"}
            </button>
          )}
        </div>
      </div>

      <ContractUeWarning clientId={clientId} totalUe={client.total_ue} isAdmin={user?.role === "admin" || !!user?.is_master_admin} onFixed={load} />
      {ueSummary && ueSummary.total_ue > 0 && ueSummary.remaining_ue <= 0 && (
        <>
          <UnicornCelebration clientId={clientId} />
          <div
            className="border border-emerald-200 bg-emerald-50 px-5 py-4 mb-6 flex items-center justify-between gap-4 flex-wrap"
            data-testid="client-completed-banner"
          >
            <div className="flex items-center gap-3">
              <PartyPopper size={20} strokeWidth={1.5} className="text-emerald-600" />
              <div>
                <div className="text-sm font-bold text-emerald-800">Kunde ist abgeschlossen 🎉</div>
                <div className="text-xs text-emerald-700">Alle UE verbraucht — bitte jetzt den Abschlussbericht ausstellen.</div>
              </div>
            </div>
            <button
              onClick={() => setTab("report")}
              data-testid="client-completed-report-cta"
              className="text-xs font-bold uppercase tracking-overline bg-emerald-600 text-white px-4 py-2 hover:bg-emerald-700 transition-colors"
            >
              Bericht ausstellen →
            </button>
          </div>
        </>
      )}

      {myPhase && (() => {
        const ho = ueSummary?.schedule_plan?.coaches?.find((c) => c.coach_user_id === user?.user_id && c.deadline);
        const fin = ho?.deadline || myPhase.finish_date;
        return (
        <div
          className={`border px-5 py-4 mb-6 text-sm flex items-center gap-3 ${
            myPhase.status === "laufend" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
          data-testid="my-phase-callout"
        >
          <CalendarClock size={16} strokeWidth={1.5} />
          <div>
            {myPhase.status === "laufend" ? (
              <>Du solltest mit {myPhase.phase_label ? <b>{myPhase.phase_label}</b> : "deinem Anteil"} hiermit bis <b>{formatDeShort(fin)}</b> fertig sein{ho ? <> und an <b>{ho.next_name}</b> übergeben</> : ""}.</>
            ) : (
              <>Dein Anteil ({myPhase.phase_label}) ist demnächst dran — voraussichtlich ab <b>{formatDeShort(myPhase.start_date)}</b>, Übergabe bis <b>{formatDeShort(fin)}</b>.</>
            )}
          </div>
        </div>
        );
      })()}

      {/* Coaching-Pensum: unterschriebene UE, fehlende UE, erforderliche UE/Woche — prominent oben */}
      {ueSummary && ueSummary.total_ue > 0 && (
        <SignedPacingCard ueSummary={ueSummary} clientId={clientId} canSuggest={(cid) => canManageClients || cid === user?.user_id} canEditDeadline={canManageClients} onChanged={load} />
      )}

      {/* ============ Tab-Navigation ============ */}
      {(() => {
        const group = TAB_GROUPS.find((g) => g.subs.some((s) => s.k === tab)) || TAB_GROUPS[0];
        return (
          <>
            <div className="mt-8 flex flex-wrap gap-x-1 border-b border-slate-200" data-testid="avgs-tabs">
              {TAB_GROUPS.map(({ k, l, i: Ic, subs }) => (
                <button key={k} onClick={() => setTab(subs[0].k)} data-testid={`tab-${k}`}
                  className={`px-3 py-3 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${group.k === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                  <Ic size={14} strokeWidth={1.5} />{l}
                  {k === "chat" && chatUnread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="tab-chat-badge">{chatUnread}</span>}
                </button>
              ))}
            </div>
            {group.subs.length > 1 && (
              <div className="flex flex-wrap gap-1 pt-4" data-testid="avgs-subtabs">
                {group.subs.map((s) => (
                  <button key={s.k} onClick={() => setTab(s.k)} data-testid={`subtab-${s.k}`}
                    className={`px-3 py-1.5 text-xs font-medium border transition-colors ${tab === s.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>{s.l}</button>
                ))}
              </div>
            )}
          </>
        );
      })()}

      <div className="pt-6">
      {tab === "overview" && (
        <div className="space-y-6" data-testid="tab-overview-content">
          {canManageClients && <TraegerStatusCard kind="client" personId={clientId} onlyMissing />}
          {/* Measure Sections (Abschnitte) + Onboarding/Offboarding als reguläre Bestandteile */}
          {Array.isArray(client.sections) && client.sections.length > 0 && (() => {
            const rows = [];
            // Neues Modell (ob_legacy_pricing=false): Onboarding/Offboarding werden NICHT im
            // Vertrag festgelegt, sondern erst hier in der Akte rein intern zugewiesen — daher
            // Zeile auch bei UE-Ziel 0 zeigen, damit man sie überhaupt zuweisen kann. Genauso bei
            // Bestandskunden (Legacy), wenn schon UE gebucht/unterschrieben wurden, OBWOHL nie ein
            // Ziel gesetzt wurde — sonst verschwindet die Zeile komplett und die gebuchte UE wirkt
            // "verschwunden" (siehe Fall Tamra).
            if (Number(client.onboarding_ue) > 0 || !client.ob_legacy_pricing || Number(client.onboarding_ue_completed) > 0) {
              rows.push({
                kind: "onboarding", section_id: null, module_name: null, name: "Onboarding",
                ue_target: Number(client.onboarding_ue) || 0, ue_completed: Number(client.onboarding_ue_completed) || 0,
                coach_user_id: client.onboarding_user_id,
              });
            }
            for (const s of client.sections) rows.push({ kind: "section", ...s });
            if (Number(client.offboarding_ue) > 0 || !client.ob_legacy_pricing || Number(client.offboarding_ue_completed) > 0) {
              rows.push({
                kind: "offboarding", section_id: null, module_name: null, name: "Offboarding",
                ue_target: Number(client.offboarding_ue) || 0, ue_completed: Number(client.offboarding_ue_completed) || 0,
                coach_user_id: client.offboarding_user_id,
              });
            }
            // Bei Kunden im neuen Carve-out-Modell (ob_legacy_pricing=false) sind Onboarding/
            // Offboarding-UE bereits Teil des Coaching-UE-Kontingents (nicht additiv) — die
            // Kopfzeile zeigt daher das tatsächliche Vertrags-Kontingent statt der Summe aller
            // Zeilen, damit sie nicht höher wirkt als das UE-Kontingent selbst.
            const totalTarget = client.ob_legacy_pricing
              ? rows.reduce((s, x) => s + (Number(x.ue_target) || 0), 0)
              : Number(client.total_ue) || 0;
            const totalDone = rows.reduce((s, x) => s + (Number(x.ue_completed) || 0), 0);
            return (
              <div className="border border-slate-200 bg-white" data-testid="client-sections-panel">
                <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">AVGS-Maßnahme</div>
                    <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Abschnitte &amp; Coach-Zuweisung</div>
                    {!client.ob_legacy_pricing && (
                      <div className="text-[10px] text-slate-400 mt-0.5">Onboarding/Offboarding-Zuweisung ist rein intern — der Vertrag wird dadurch nicht verändert.</div>
                    )}
                    {client.ob_legacy_pricing && user?.role === "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button disabled={migratingPricing} data-testid="migrate-carveout-pricing-btn"
                            className="text-[10px] text-amber-600 hover:text-amber-800 underline mt-0.5 disabled:opacity-50">
                            {migratingPricing ? "…" : "Onboarding/Offboarding zählen aktuell ZUSÄTZLICH zum Kontingent — auf neues Modell umstellen?"}
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="migrate-carveout-pricing-confirm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Auf neues Onboarding/Offboarding-Modell umstellen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Aktuell zählen Onboarding ({Number(client.onboarding_ue) || 0} UE) und Offboarding
                              ({Number(client.offboarding_ue) || 0} UE) ZUSÄTZLICH zum Modul-Kontingent — das
                              Gesamt-Kontingent wird dadurch künstlich auf <b>{totalTarget} UE</b> erhöht. Nach der
                              Umstellung kommen sie stattdessen AUS dem bestehenden Kontingent (interne Verteilung,
                              kein zusätzlicher Posten) und das Gesamt-Kontingent wird auf{" "}
                              <b>{client.sections.reduce((s, x) => s + (Number(x.ue_target) || 0), 0)} UE</b> korrigiert.
                              Die Onboarding/Offboarding-UE-Werte selbst bleiben erhalten.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction onClick={migrateToCarveout}>Ja, umstellen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <div className="text-xs font-mono text-slate-500">
                    {totalTarget} UE gesamt geplant
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {rows.map((s, idx) => {
                    const coach = coaches.find((c) => c.user_id === s.coach_user_id);
                    const coachName = coach?.name || client.staff_names?.[s.coach_user_id];
                    const pct = s.ue_target ? Math.min(100, Math.round(((s.ue_completed || 0) / s.ue_target) * 100)) : 0;
                    const rowKey = s.section_id || `${s.kind}-${idx}`;
                    return (
                      <div key={rowKey} data-testid={`client-section-row-${rowKey}`} className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1fr_140px_180px] gap-3 items-center">
                        <div className="min-w-0">
                          {s.kind !== "section" ? (
                            <span className="text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 bg-amber-400 text-[#0A0A0A] mr-2">
                              {s.kind === "onboarding" ? "Onboarding" : "Offboarding"}
                            </span>
                          ) : s.module_name && (
                            <span className="text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 bg-[#00CCC9] text-[#0A0A0A] mr-2">
                              {s.module_name}
                            </span>
                          )}
                          <span className="text-sm font-medium text-slate-900">{s.name}</span>
                        </div>
                        <div>
                          <div className="flex items-baseline gap-2">
                            <EditableUeCell
                              clientId={clientId}
                              kind={s.kind}
                              sectionId={s.section_id}
                              ueCompleted={s.ue_completed}
                              ueTarget={s.ue_target}
                              onSaved={load}
                              readOnly={!canManageClients}
                              testId={`ue-completed-${rowKey}`}
                            />
                            <span className="text-xs text-slate-500">/ {s.ue_target || 0} UE</span>
                          </div>
                          {s.ue_target === 0 && (s.ue_completed || 0) > 0 && (
                            <div className="text-[10px] text-amber-700 mt-1" data-testid={`ue-no-target-warning-${rowKey}`}>
                              {s.ue_completed} UE gebucht — noch kein Ziel gesetzt
                            </div>
                          )}
                          <div className="mt-1 h-1 bg-slate-100 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-[#00CCC9]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="text-xs">
                          {coachName ? (
                            <span className="text-slate-700 truncate block">👤 {coachName}</span>
                          ) : s.coach_user_id ? (
                            <span className="text-slate-400 italic">{s.coach_user_id}</span>
                          ) : (
                            <span className="text-slate-400 italic">noch kein Coach</span>
                          )}
                          {s.kind !== "section" && !client.ob_legacy_pricing && (
                            <OnboardingAssignEditor
                              clientId={clientId}
                              kind={s.kind}
                              ueTarget={s.ue_target}
                              coachUserId={s.coach_user_id}
                              coaches={coaches}
                              onSaved={load}
                              readOnly={!canManageClients}
                              testId={`assign-${s.kind}`}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* UE-Zuordnungs-Warnung: Einträge ohne (oder mit veraltetem) section_id */}
                {(client.unassigned_ue || 0) > 0 && (
                  <div className="px-5 py-3 border-t border-amber-200 bg-amber-50 text-amber-900 text-xs" data-testid="unassigned-ue-warning">
                    <b>{client.unassigned_ue} UE nicht zugeordnet</b> — es gibt Einträge oder unterschriebene
                    Termine für diesen Kunden ohne passenden Abschnitt. Bitte im Bereich <b>Einträge</b> bzw.
                    im Tab <b>Termine</b> (Dropdown je Termin) den korrekten Abschnitt setzen, damit die UE
                    dem richtigen Modul angerechnet wird — oder die erledigte UE oben direkt eintragen.
                  </div>
                )}
                {/* Debug/Info: Live totals for coaches */}
                {client.ue_totals && (
                  <div className="px-5 py-2 border-t border-slate-100 text-[11px] text-slate-500 font-mono flex flex-wrap gap-4" data-testid="ue-totals-info">
                    <span>Einträge · {client.ue_totals.entries_ue} UE</span>
                    <span>Unterschriebene Termine · {client.ue_totals.signed_appointments_ue} UE</span>
                    <span>Summe Abschnitte{client.ob_legacy_pricing ? " (inkl. Onboarding/Offboarding)" : ""} · {totalDone} UE</span>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Kontakt / Gutschein / Interne Notizen */}
          {Array.isArray(client.sections) && client.sections.length > 0 && (
            <div data-testid="overview-abschnitte">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Abschnitte &amp; Ergebnisse</div>
              <SectionDeliverablesPanel clientId={clientId} />
            </div>
          )}
          <ClientAkteExtras client={client} onChanged={load} />
          {/* Dozenten-Feedback */}
          <DozentenFeedbackPanel clientId={clientId} currentUser={user} />
          <AvgsFeedbackPanel clientId={clientId} />
          <QuestionnaireCard personKind="client" personId={clientId} />
          <AppointmentOutcomeCard clientId={clientId} canDecide={(user?.all_roles || [user?.role]).some((r) => r === "admin" || r === "manager")} />
          <PlannerHistoryCard clientId={clientId} />
          {(canDocCheck || (user?.all_roles || [user?.role]).includes("manager")) && <ComplaintFormButton kind="client" personId={clientId} />}
        </div>
      )}

      {tab === "chat" && (
        <div data-testid="tab-chat-content">
          <ClientChatPanel clientId={clientId} title={`Chat mit ${client.name}`} />
        </div>
      )}

      {tab === "kalender" && (
        <div data-testid="tab-kalender-content">
          <AvgsClientCalendar appointments={appointments} />
        </div>
      )}

      {tab === "bewerbungen" && (
        <div className="space-y-6" data-testid="tab-bewerbungen-content">
          <JobApplicationsPanel clientId={clientId} />
          <div data-testid="tab-cv-content">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Lebenslauf</div>
            <CvProfileReadOnly clientId={clientId} />
          </div>
        </div>
      )}

      {tab === "ziele" && (
        <div data-testid="tab-ziele-content">
          <ClientGoalsPanel clientId={clientId} clientName={client.name} />
        </div>
      )}

      {tab === "termine" && (
        <TermineTab appointments={appointments} client={client} onDownload={() => downloadFile(`/clients/${clientId}/appointments/pdf`, `anwesenheitsliste_${client.name}.pdf`)} onChanged={load} />
      )}

      {tab === "doc" && (
        <DocsTab client={client} docs={docs} onChanged={load} />
      )}
      {tab === "interim_report" && (
        <ReportTab kind="interim" client={client} report={interimReport} docsCount={docs.length} onGenerated={load}
          onDownload={() => downloadFile(`/clients/${clientId}/interim-report/pdf`, `Zwischenbericht_${client.customer_number || client.client_id}.pdf`)} />
      )}
      {tab === "report" && (
        <ReportTab kind="final" client={client} report={report} docsCount={docs.length} onGenerated={load}
          onDownload={() => downloadFile(`/clients/${clientId}/final-report/pdf`, `Abschlussbericht_${client.customer_number || client.client_id}.pdf`)} />
      )}
      {tab === "unterlagen" && (
        <div className="space-y-6" data-testid="client-teilnehmerunterlagen">
          {canManageClients && <TraegerStatusCard kind="client" personId={clientId} inline onChanged={() => setDocsKey((k) => k + 1)} />}
          <CoachingDokuCard clientId={clientId} />
          <ClientCertificates clientId={clientId} canManage={canManageClients} />
          {(canDocCheck || canManageClients) && <ClientDocumentsPanel key={docsKey} clientId={clientId} />}
        </div>
      )}
      {tab === "branding" && (
        <div className="space-y-6" data-testid="client-branding-tab">
          <ClientBrandingPanel clientId={clientId} />
        </div>
      )}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, text }) {
  return (
    <div className="border border-slate-200 bg-white p-10 text-center">
      <Icon size={28} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
      <div className="text-sm font-medium text-slate-800 mb-1">{title}</div>
      <div className="text-xs text-slate-500 max-w-md mx-auto">{text}</div>
    </div>
  );
}

function DocsTab({ client, docs, onChanged }) {
  const [date, setDate] = useState(todayIso());
  const [keywords, setKeywords] = useState("");
  const [aiText, setAiText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const existing = docs.find((d) => d.date === date);
  const docLines = aiText.split("\n").filter((l) => l.trim()).length;
  const tooLong = docLines > DOC_MAX_LINES || aiText.trim().length > DOC_MAX_CHARS;
  useEffect(() => {
    if (existing) { setKeywords(existing.keywords || ""); setAiText(existing.final_text || ""); }
    else { setKeywords(""); setAiText(""); }
  }, [date]); // eslint-disable-line

  const generate = async () => {
    if (!keywords.trim()) { toast.error("Bitte Stichworte eingeben"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/dailydocs/generate", { client_id: client.client_id, date, keywords });
      setAiText(data.ai_text);
      toast.success("KI-Doku erstellt — du kannst sie noch bearbeiten");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!aiText.trim()) { toast.error("Bitte zuerst Doku erzeugen oder eintragen"); return; }
    setSaving(true);
    try {
      await api.post("/dailydocs", { client_id: client.client_id, date, keywords, final_text: aiText });
      toast.success("Doku gespeichert");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 p-6">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-4">Stichworte → KI-Doku</div>
        <div>
          <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Datum</label>
          <GermanDateInput data-testid="doc-date"  className="input-tech font-mono mb-4" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 flex items-center gap-2">Stichworte <InfoTip k="tagesdoku" /></label>
          <textarea data-testid="doc-keywords" className="input-tech min-h-[150px] py-3 border" placeholder="Themen, Übungen, Beobachtungen, nächste Schritte…"
            value={keywords} onChange={(e) => setKeywords(e.target.value)} />
        </div>
        <button data-testid="doc-generate" onClick={generate} disabled={generating}
          className="mt-4 w-full bg-blue-700 text-white py-3 text-sm font-medium hover:bg-blue-800 disabled:opacity-50 flex items-center justify-center gap-2">
          <Sparkles size={14} strokeWidth={1.5} /> {generating ? "KI schreibt…" : "Mit Claude generieren"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-4">Doku-Text (bearbeitbar)</div>
        <textarea data-testid="doc-final-text" className="input-tech min-h-[160px] py-3 border" placeholder="Hier erscheint der KI-Text (max. 5 kurze Zeilen). Du kannst ihn bearbeiten, bevor du speicherst."
          value={aiText} maxLength={DOC_MAX_CHARS} onChange={(e) => setAiText(e.target.value)} />
        <div className={`mt-1 text-[11px] ${tooLong ? "text-red-600 font-medium" : "text-slate-500"}`} data-testid="doc-limit-counter">
          {docLines} / {DOC_MAX_LINES} Zeilen · {aiText.trim().length} / {DOC_MAX_CHARS} Zeichen{tooLong ? " – bitte kürzen" : ""}
        </div>
        <button data-testid="doc-save" onClick={save} disabled={saving || tooLong}
          className="mt-4 w-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
          <Save size={14} strokeWidth={1.5} /> {saving ? "Speichert…" : existing ? "Aktualisieren" : "Doku speichern"}
        </button>
        {docs.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">Gespeicherte Dokus ({docs.length})</div>
            <div className="space-y-1 max-h-[200px] overflow-y-auto" data-testid="docs-list">
              {docs.map((d) => (
                <button key={d.doc_id} onClick={() => setDate(d.date)}
                  className={`w-full text-left py-2 px-3 border border-slate-100 text-sm hover:bg-slate-50 ${d.date === date ? "bg-blue-50 border-blue-200" : ""}`}>
                  <span className="font-mono">{formatDeShort(d.date)}</span>
                  <span className="ml-2 text-xs text-slate-500 truncate">{(d.final_text || "").slice(0, 60)}…</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const DOC_MAX_LINES = 5;
const DOC_MAX_CHARS = 500;

function renderInlineBold(text, keyPrefix) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={`${keyPrefix}-b-${i}`}>{part}</strong> : <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>));
}

function renderReportMarkdown(text) {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n").filter((l) => l.trim() !== "");
    if (lines.length === 0) return null;
    const first = lines[0].trim();
    const isHeading = first.length < 80 && first === first.toUpperCase() && /[A-ZÄÖÜ]/.test(first) && !first.endsWith(".") && !first.startsWith("- ");
    const headingLine = isHeading ? first : null;
    const rest = isHeading ? lines.slice(1) : lines;

    const nodes = [];
    let buffer = [];
    let bulletBuffer = [];
    const flushParagraph = () => {
      if (buffer.length) {
        nodes.push(
          <p key={`p-${bi}-${nodes.length}`} className="mb-3 leading-relaxed">
            {buffer.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInlineBold(l, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
        buffer = [];
      }
    };
    const flushBullets = () => {
      if (bulletBuffer.length) {
        nodes.push(
          <ul key={`ul-${bi}-${nodes.length}`} className="list-disc pl-5 mb-3 space-y-1">
            {bulletBuffer.map((l, li) => <li key={li}>{renderInlineBold(l, `${bi}-ul-${li}`)}</li>)}
          </ul>
        );
        bulletBuffer = [];
      }
    };
    rest.forEach((line) => {
      const t = line.trim();
      if (t.startsWith("- ")) {
        flushParagraph();
        bulletBuffer.push(t.slice(2).trim());
      } else {
        flushBullets();
        buffer.push(t);
      }
    });
    flushParagraph();
    flushBullets();
    return (
      <div key={bi} className="mb-2">
        {headingLine && <div className="font-bold text-slate-900 text-[15px] mt-6 mb-2 first:mt-0">{headingLine}</div>}
        {nodes}
      </div>
    );
  });
}

function ReportTab({ kind = "final", client, report, docsCount, onGenerated, onDownload }) {
  const [generating, setGenerating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewContent, setPdfPreviewContent] = useState(null);
  const endpoint = kind === "interim" ? "interim-report" : "final-report";
  const label = kind === "interim" ? "Zwischenbericht" : "Abschlussbericht";
  const testPrefix = kind === "interim" ? "interim-report" : "report";

  const generate = async () => {
    if (docsCount === 0) { toast.error("Erstelle zuerst Tages-Dokumentationen"); return; }
    setGenerating(true);
    try {
      await api.post(`/clients/${client.client_id}/${endpoint}`, {}, { timeout: 60000 });
      toast.success(`${label} erstellt`);
      onGenerated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  const startEdit = () => { setEditText(report.content); setShowPreview(false); setEditing(true); };

  const saveEdit = async () => {
    if (!editText.trim()) { toast.error("Text darf nicht leer sein"); return; }
    setSaving(true);
    try {
      await api.put(`/clients/${client.client_id}/${endpoint}`, { content: editText.trim() });
      toast.success(`${label} gespeichert`);
      setEditing(false);
      onGenerated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const toggleRelease = async () => {
    setReleasing(true);
    try {
      if (report?.released_to_client) {
        await api.put(`/clients/${client.client_id}/${endpoint}/unrelease`);
        toast.success("Freigabe zurückgenommen");
      } else {
        await api.put(`/clients/${client.client_id}/${endpoint}/release`);
        toast.success("Für Kunde freigegeben — er sieht den Bericht jetzt in seinem Dashboard");
      }
      onGenerated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setReleasing(false); }
  };

  const openPdfPreview = async () => {
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    setPdfPreviewContent(null);
    try {
      const { data } = await api.get(`/clients/${client.client_id}/${endpoint}/formatted-preview`);
      setPdfPreviewContent(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPdfPreviewOpen(false);
    } finally { setPdfPreviewLoading(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-8 max-w-3xl" data-testid={`${testPrefix}-tab`}>
      <Dialog open={pdfPreviewOpen} onOpenChange={setPdfPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`${testPrefix}-pdf-preview-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light tracking-tighter">
              PDF-Vorschau · {label}
            </DialogTitle>
          </DialogHeader>
          {pdfPreviewLoading ? (
            <div className="py-10 text-center text-sm text-slate-500 italic" data-testid={`${testPrefix}-pdf-preview-loading`}>
              KI formatiert den Text für das PDF…
            </div>
          ) : pdfPreviewContent ? (
            <>
              <div className="text-xs text-slate-500 mb-2">
                So wird der Text im PDF erscheinen — automatisch von der KI formatiert (Absätze, Überschriften, Aufzählungen), ohne den Inhalt zu verändern.
              </div>
              <div data-testid={`${testPrefix}-pdf-preview-content`} className="prose prose-sm max-w-none text-slate-800 font-sans leading-relaxed bg-slate-50 border border-slate-200 p-6">
                {renderReportMarkdown(pdfPreviewContent.formatted_content)}
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  data-testid={`${testPrefix}-pdf-preview-confirm-download`}
                  onClick={() => { setPdfPreviewOpen(false); onDownload(); }}
                  className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 flex items-center gap-2"
                >
                  <Download size={14} strokeWidth={1.5} /> PDF jetzt herunterladen
                </button>
                <button
                  data-testid={`${testPrefix}-pdf-preview-close`}
                  onClick={() => setPdfPreviewOpen(false)}
                  className="border border-slate-300 text-slate-700 px-6 py-3 text-sm font-medium hover:bg-slate-100"
                >
                  Abbrechen
                </button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">
        {kind === "interim" ? "Teilnehmerbezogener Zwischenbericht" : "Teilnehmerbezogener Bericht"}
      </div>
      <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-5">
        KI generiert den {label} aus allen {docsCount} Tages-Dokus.
      </h2>
      {kind === "interim" && (
        <p className="text-sm text-slate-500 mb-5 -mt-3">
          Momentaufnahme des bisherigen Verlaufs — die Maßnahme läuft noch weiter.
        </p>
      )}
      <div className="flex gap-3 mb-6 flex-wrap">
        <button data-testid={`${testPrefix}-generate`} onClick={generate} disabled={generating || docsCount === 0}
          className="bg-blue-700 text-white px-6 py-3 text-sm font-medium hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2">
          <Sparkles size={14} strokeWidth={1.5} /> {generating ? "KI schreibt…" : report ? "Neu generieren" : "Bericht generieren"}
        </button>
        {report && (
          <>
            <button data-testid={`${testPrefix}-edit`} onClick={startEdit} disabled={editing}
              className="border border-slate-300 text-slate-700 px-6 py-3 text-sm font-medium hover:bg-slate-100 disabled:opacity-50 flex items-center gap-2">
              <FileText size={14} strokeWidth={1.5} /> Bearbeiten
            </button>
            <button data-testid={`${testPrefix}-download`} onClick={openPdfPreview}
              className="border border-slate-300 text-slate-700 px-6 py-3 text-sm font-medium hover:bg-slate-100 flex items-center gap-2">
              <Download size={14} strokeWidth={1.5} /> PDF herunterladen
            </button>
            <button
              data-testid={`${testPrefix}-release-toggle`}
              onClick={toggleRelease}
              disabled={releasing}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 disabled:opacity-50 ${
                report.released_to_client
                  ? "border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
            >
              {report.released_to_client ? "✓ Für Kunde freigegeben · zurücknehmen" : "Für Kunde freigeben"}
            </button>
          </>
        )}
      </div>
      {report && report.released_to_client && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
          <b>Freigegeben:</b> Der Kunde sieht diesen Bericht jetzt in seinem Dashboard.
        </div>
      )}
      {report && (
        <div className="border-t border-slate-100 pt-6 mt-6">
          <div className="text-xs font-mono text-slate-500 mb-3">
            Erstellt am: {formatDeShortDateTime(report.generated_at)}
            {report.edited_at && <span> · zuletzt bearbeitet: {formatDeShortDateTime(report.edited_at)}</span>}
          </div>
          {editing ? (
            <div data-testid={`${testPrefix}-edit-form`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-xs text-slate-500">
                  Formatierung: <code className="bg-slate-100 px-1">**fett**</code>, <code className="bg-slate-100 px-1">- Punkt</code> für Aufzählungen, GROSSBUCHSTABEN-Zeile = Überschrift, Leerzeile = neuer Absatz.
                </span>
                <button type="button" data-testid={`${testPrefix}-edit-preview-toggle`} onClick={() => setShowPreview(!showPreview)}
                  className="text-xs font-bold uppercase tracking-overline text-slate-600 hover:text-slate-900">
                  {showPreview ? "Zurück zum Text" : "Vorschau anzeigen"}
                </button>
              </div>
              {showPreview ? (
                <div data-testid={`${testPrefix}-edit-preview`} className="w-full min-h-[420px] text-sm text-slate-800 font-sans bg-slate-50 border border-slate-300 p-6 overflow-auto">
                  {renderReportMarkdown(editText)}
                </div>
              ) : (
                <textarea
                  data-testid={`${testPrefix}-edit-textarea`}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full min-h-[420px] text-sm text-slate-800 font-sans leading-relaxed bg-white border border-slate-300 p-6 focus:outline-none focus:border-slate-500"
                />
              )}
              <div className="flex gap-3 mt-3">
                <button data-testid={`${testPrefix}-edit-save`} onClick={saveEdit} disabled={saving}
                  className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
                  <Save size={14} strokeWidth={1.5} /> {saving ? "Speichert…" : "Speichern"}
                </button>
                <button data-testid={`${testPrefix}-edit-cancel`} onClick={() => setEditing(false)} disabled={saving}
                  className="border border-slate-300 text-slate-700 px-6 py-3 text-sm font-medium hover:bg-slate-100 disabled:opacity-50">
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div data-testid={`${testPrefix}-content`} className="prose prose-sm max-w-none text-slate-800 font-sans leading-relaxed bg-slate-50 border border-slate-200 p-6">
              {renderReportMarkdown(report.content)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-100 p-3">
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900 mt-1">{value}</div>
    </div>
  );
}

const PACE_LABEL = {
  green: { label: "Auf Kurs", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  orange: { label: "Achtung", cls: "bg-amber-50 text-amber-800 border-amber-300", dot: "bg-amber-500" },
  red: { label: "Kritisch", cls: "bg-red-50 text-red-800 border-red-300", dot: "bg-red-500" },
  gray: { label: "Kein Kontingent", cls: "bg-slate-100 text-slate-700 border-slate-300", dot: "bg-slate-400" },
};

function SignedPacingCard({ ueSummary, clientId, canSuggest, canEditDeadline, onChanged }) {
  const req = ueSummary.required_ue_per_week_signed || 0;
  const unsignedCount = ueSummary.appointments_completed_unsigned || 0;
  const plannedTotal = ueSummary.appointments_planned_total || 0;
  const ueAwaitingSignature = ueSummary.ue_completed_unsigned || 0;
  const ueScheduledFuture = ueSummary.ue_scheduled_future || 0;
  const ueToSchedule = ueSummary.ue_not_yet_scheduled || 0;
  const timeIsUp = ueSummary.ended || ueSummary.weeks_remaining <= 0;
  const status = (timeIsUp && (ueSummary.remaining_ue > 0 || unsignedCount > 0))
    ? "red"
    : ueToSchedule > 0
    ? (ueToSchedule / Math.max(ueSummary.weeks_remaining || 0, 1 / 7) > 12 ? "red" : "orange")
    : unsignedCount > 0
    ? "orange"
    : "green";
  const p = PACE_LABEL[status];
  const sessionsPerWeek = req > 0 ? Math.max(1, Math.ceil(req / 4)) : 0;
  let hint;
  if (timeIsUp && unsignedCount > 0) {
    hint = `Achtung: ${unsignedCount} Termin${unsignedCount > 1 ? "e" : ""} ${unsignedCount > 1 ? "warten" : "wartet"} noch auf die Unterschrift` +
      (ueSummary.ended ? " — der Bewilligungszeitraum ist bereits vorbei!" : ", der Bewilligungszeitraum endet heute!");
  } else if (ueSummary.ended && ueSummary.remaining_ue > 0) {
    hint = "Der Bewilligungszeitraum ist abgelaufen und es fehlen noch unterschriebene UE.";
  } else if (ueSummary.remaining_ue <= 0) {
    hint = "Alle bewilligten UE sind bereits unterschrieben.";
  } else if (ueToSchedule > 0) {
    hint = `Von den fehlenden UE ist für ${ueToSchedule} UE noch GAR KEIN Termin im Kalender — diese musst du zusätzlich neu terminieren.`;
  } else {
    hint = `Alle fehlenden UE stehen bereits als Termin im Bewilligungszeitraum — du musst nichts mehr terminieren.${unsignedCount > 0 ? ` Bitte noch ${unsignedCount} gehaltene${unsignedCount > 1 ? "" : "n"} Termin${unsignedCount > 1 ? "e" : ""} unterschreiben lassen.` : " Es fehlt nur noch die Durchführung."}`;
  }
  return (
    <div className={`border mb-8 p-5 ${p.cls}`} data-testid="signed-pacing-card">
      <div className="flex items-center gap-3 mb-4">
        <span className={`inline-block w-3 h-3 rounded-full ${p.dot}`}></span>
        <div className="text-xs font-bold uppercase tracking-overline opacity-80">Coaching-Pensum (unterschriebene UE)</div>
        <InfoTip k="pacing" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-5">
        <PaceMetric label="Unterschriebene UE" value={`${ueSummary.used_ue} UE`} testId="signed-pace-used" />
        <PaceMetric label="Fehlende UE (gesamt)" value={`${ueSummary.remaining_ue} UE`} testId="signed-pace-missing" />
        <PaceMetric label="Termine geplant (gesamt)" value={`${plannedTotal}`} testId="signed-pace-planned-count" />
        <PaceMetric label="Noch bis Ende Bewilligung"
          value={ueSummary.ended ? "abgelaufen" : `${ueSummary.weeks_remaining} Wochen`}
          testId="signed-pace-weeks-remaining" />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-overline opacity-70 mb-2">Aufschlüsselung der fehlenden {ueSummary.remaining_ue} UE</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="border border-current/20 px-3 py-2" data-testid="signed-pace-unsigned-count">
          <div className="text-[10px] opacity-70">Wartet auf Unterschrift</div>
          <div className="font-heading text-lg font-light tracking-tighter">{ueAwaitingSignature} UE</div>
          <div className="text-[10px] opacity-60">{unsignedCount} Termin{unsignedCount === 1 ? "" : "e"} bereits gehalten</div>
        </div>
        <div className="border border-current/20 px-3 py-2" data-testid="signed-pace-scheduled-future">
          <div className="text-[10px] opacity-70">Geplant, noch nicht gehalten</div>
          <div className="font-heading text-lg font-light tracking-tighter">{ueScheduledFuture} UE</div>
          <div className="text-[10px] opacity-60">steht schon im Kalender</div>
        </div>
        <div className="border-2 border-current px-3 py-2" data-testid="signed-pace-to-schedule">
          <div className="text-[10px] opacity-70 font-bold">Muss noch terminiert werden</div>
          <div className="font-heading text-lg font-bold tracking-tighter">{ueToSchedule} UE</div>
          <div className="text-[10px] opacity-60">dafür existiert noch KEIN Termin</div>
        </div>
      </div>
      <div className="text-xs leading-relaxed opacity-90 mb-4" data-testid="signed-pace-hint">{hint}</div>

      <SchedulePlan plan={ueSummary.schedule_plan} clientId={clientId} canSuggest={canSuggest} canEditDeadline={canEditDeadline} onChanged={onChanged} />
    </div>
  );
}

function PaceStrip({ pace }) {
  const p = PACE_LABEL[pace.pace_status] || PACE_LABEL.gray;
  const hint = pace.ended
    ? "Coaching-Zeitraum ist abgelaufen — bitte End-Datum verlängern oder Kontingent anpassen."
    : pace.pace_status === "red"
    ? `Zu wenig Termine geplant. Es müsste ab jetzt ~${pace.required_ue_per_week} UE/Woche gecoacht werden — das sind ${Math.ceil(pace.required_ue_per_week / 4)} Termine pro Woche.`
    : pace.pace_status === "orange"
    ? `Empfohlener Rhythmus: ~${pace.required_ue_per_week} UE/Woche (${Math.max(1, Math.ceil(pace.required_ue_per_week / 4))} Termin(e)/Woche), damit der Zeitraum eingehalten wird.`
    : pace.pace_status === "green"
    ? `Alles im Rahmen — ${pace.booked_ue} von ${pace.total_ue} UE bereits verplant.`
    : `Kein UE-Kontingent gesetzt. Admin kann das Kontingent oben im Kunden-Datensatz nachtragen.`;
  return (
    <div className={`border-t ${p.cls} p-5`} data-testid="pace-strip">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`inline-block w-3 h-3 rounded-full ${p.dot}`}></span>
          <div>
            <div className="text-xs font-bold uppercase tracking-overline opacity-80">Coaching-Plan</div>
            <div className="font-heading text-2xl font-light tracking-tighter mt-1" data-testid="pace-status">{p.label}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 flex-1 min-w-[320px]">
          <PaceMetric label="Zeit übrig" value={pace.ended ? "abgelaufen" : `${pace.weeks_remaining.toFixed(1)} Wochen`} testId="pace-weeks" />
          <PaceMetric label="Bereits verplant" value={`${pace.booked_ue} UE`} sub={`(${pace.booked_pct.toFixed(0)}%)`} testId="pace-booked" />
          <PaceMetric label="Noch zu buchen" value={`${pace.unbooked_ue} UE`} testId="pace-unbooked" />
          <PaceMetric label="Empfohlen /Woche" value={pace.required_ue_per_week > 0 ? `${pace.required_ue_per_week} UE` : "—"} testId="pace-per-week" />
        </div>
      </div>
      <div className="text-xs mt-4 leading-relaxed opacity-90" data-testid="pace-hint">{hint}</div>
    </div>
  );
}

function SchedulePlan({ plan, clientId, canSuggest, canEditDeadline, onChanged }) {
  if (!plan || !plan.coaches.length) return null;
  const fmt = (n) => Number(n).toLocaleString("de-DE", { maximumFractionDigits: 1 });
  return (
    <div className="border-t border-current/15 pt-4" data-testid="schedule-plan">
      <div className="text-[10px] font-bold uppercase tracking-overline opacity-70 mb-2 flex items-center gap-2">Terminierungsplan – pünktlich fertig bis {plan.end ? formatDeShort(plan.end) : "—"} <InfoTip k="schedule-plan" /></div>
      {plan.all_on_time && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-medium" data-testid="handover-all-on-time">
          <HummingbirdIcon size={22} /> Alle Coaches haben pünktlich übergeben.
        </div>
      )}
      <div className="text-xs leading-relaxed opacity-90 mb-3" data-testid="schedule-plan-summary">
        {plan.remaining_ue <= 0 ? "Alle UE sind unterschrieben." : plan.expired ? <b>Der Bewilligungszeitraum ist abgelaufen – es fehlen noch {fmt(plan.remaining_ue)} UE.</b> : (
          <>Noch <b>{fmt(plan.to_schedule_ue)} UE</b> müssen bei den Coaches terminiert werden ({fmt(plan.remaining_ue)} UE fehlen in den Abschnitten{plan.extra_ue > 0 ? `, zzgl. ${fmt(plan.extra_ue)} UE On-/Offboarding` : ""}). Regel: mindestens {plan.min_sessions_per_week} Termine pro Woche, max. {fmt(plan.max_ue_per_session)} UE pro Termin.</>
        )}
      </div>
      <div className="space-y-2">
        {plan.coaches.map((c, i) => (
          <div key={c.coach_user_id || "none"} className="border border-current/20 px-3 py-2 text-xs" data-testid={`schedule-plan-coach-${c.coach_user_id || "none"}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span><span className="opacity-50 mr-1">{i + 1}.</span><b>{c.coach_name}</b></span>
              <span className="font-mono" data-testid={`schedule-plan-window-${c.coach_user_id || "none"}`}>
                {c.done ? "erledigt" : c.window_start ? `${formatDeShort(c.window_start)} – ${formatDeShort(c.window_end)}` : "kein Zeitraum"}
              </span>
            </div>
            <HandoverLine c={c} clientId={clientId} canEdit={canEditDeadline} onChanged={onChanged} />
            {!c.done && (
              <div className="opacity-90 mt-1 leading-relaxed">
                Noch {fmt(c.remaining_ue)} UE (davon <b>{fmt(c.to_schedule_ue)} UE noch terminieren</b>)
                {c.to_schedule_ue > 0 ? <>{" "}· {fmt(c.ue_per_week)} UE/Woche →{" "}
                  <span className={c.more_sessions_needed ? "font-bold text-red-700" : ""} data-testid={`schedule-plan-sessions-${c.coach_user_id || "none"}`}>
                    {c.more_sessions_needed ? "Mehr Termine nötig: " : ""}{c.sessions_per_week} Termine/Woche à {fmt(c.ue_per_session)} UE
                  </span></>
                  : <span className="text-emerald-700 font-medium" data-testid={`schedule-plan-sessions-${c.coach_user_id || "none"}`}> · alles terminiert ✓</span>}
                {c.coach_user_id && c.to_schedule_ue > 0 && !plan.expired && canSuggest(c.coach_user_id) && (
                  <span className="ml-2"><ScheduleSuggestButton clientId={clientId} coach={c} onApplied={onChanged} /></span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HandoverLine({ c, clientId, canEdit, onChanged }) {
  const id = c.coach_user_id || "none";
  if (!c.deadline && !canEdit) return null;
  const save = async (date) => {
    try { await api.put(`/clients/${clientId}/handover-deadline`, { coach_user_id: c.coach_user_id, date }); toast.success(date ? "Übergabe-Frist gespeichert" : "Automatische Frist wiederhergestellt"); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const status = c.handed_over
    ? (c.on_time
      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid={`handover-ontime-${id}`}><HummingbirdIcon size={20} flapping={false} /> pünktlich übergeben am {formatDeShort(c.handed_over_on)}</span>
      : <span className="text-amber-800 font-medium" data-testid={`handover-late-${id}`}>verspätet übergeben{c.handed_over_on ? ` am ${formatDeShort(c.handed_over_on)}` : ""}</span>)
    : c.overdue ? <span className="text-red-700 font-bold" data-testid={`handover-overdue-${id}`}>Frist überschritten</span> : null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1" data-testid={`handover-line-${id}`}>
      <span>Übergabe an <b>{c.next_name}</b> bis</span>
      {canEdit
        ? <span className="w-[130px]"><GermanDateInput data-testid={`handover-deadline-${id}`} className="!py-0.5 text-xs" value={c.deadline || ""} onChange={(e) => save(e.target.value)} /></span>
        : <b className="font-mono" data-testid={`handover-deadline-${id}`}>{c.deadline ? formatDeShort(c.deadline) : "—"}</b>}
      {c.deadline_manual && <span className="opacity-60">(manuell{canEdit && <> · <button className="underline" onClick={() => save(null)} data-testid={`handover-reset-${id}`}>automatisch</button></>})</span>}
      {status}
    </div>
  );
}

function PaceMetric({ label, value, sub, testId }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-overline opacity-70">{label}</div>
      <div className="font-heading text-xl font-light tracking-tighter mt-1" data-testid={testId}>
        {value} {sub && <span className="text-xs opacity-60">{sub}</span>}
      </div>
    </div>
  );
}

function DozentenFeedbackPanel({ clientId, currentUser }) {
  const canSeeHistory = ["admin", "manager"].includes(currentUser?.role);
  const [feedback, setFeedback] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(canSeeHistory);
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!canSeeHistory) return;
    api.get(`/clients/${clientId}/feedback`).then((r) => setFeedback(r.data || [])).catch(() => setFeedback([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const send = async () => {
    if (!text.trim()) return toast.error("Bitte Feedback eingeben");
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/feedback`, { text: text.trim() });
      toast.success("Feedback gespeichert — sichtbar für Admin/Bildungsmanagement");
      setText("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const remove = async (fb) => {
    if (!window.confirm("Feedback-Eintrag wirklich löschen?")) return;
    try {
      await api.delete(`/clients/${clientId}/feedback/${fb.feedback_id}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="dozenten-feedback-panel">
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
          <MessageSquare size={12} strokeWidth={1.5} /> Dozenten-Feedback
        </div>
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Interne Rückmeldung</div>
      </div>
      <div className="p-5 space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Rückmeldung zum Kunden für Admin / Bildungsmanagement…"
          className="input-tech resize-none"
          data-testid="dozenten-feedback-input"
        />
        <button
          onClick={send}
          disabled={sending}
          data-testid="dozenten-feedback-submit"
          className="px-4 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50"
        >
          {sending ? "Speichert…" : "Feedback senden"}
        </button>
        {!canSeeHistory && (
          <p className="text-[11px] text-slate-400 italic">Nur Admin/Bildungsmanagement sehen den Feedback-Verlauf.</p>
        )}
        {canSeeHistory && (
          <div className="pt-3 border-t border-slate-100" data-testid="dozenten-feedback-history">
            {loading ? (
              <div className="text-xs text-slate-400">Lädt…</div>
            ) : feedback.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Noch kein Feedback erfasst.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {feedback.map((f) => (
                  <li key={f.feedback_id} className="py-3 flex items-start justify-between gap-3" data-testid={`feedback-row-${f.feedback_id}`}>
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono text-slate-400 mb-1">
                        {f.author_name || "—"} · {formatDeShortDateTime(f.created_at)}
                      </div>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap">{f.text}</div>
                    </div>
                    <button onClick={() => remove(f)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`feedback-delete-${f.feedback_id}`}>
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientAkteExtras({ client, onChanged }) {
  const [contract, setContract] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(client.internal_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [reinviting, setReinviting] = useState(false);

  const loadContract = () => {
    api.get(`/clients/${client.client_id}/contract/info`).then((r) => setContract(r.data)).catch(() => setContract(null));
  };
  useEffect(() => { loadContract(); }, [client.client_id]); // eslint-disable-line

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Nur PDF erlaubt"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Datei zu groß (max 8 MB)"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",", 2)[1]);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      await api.post(`/clients/${client.client_id}/contract`, { filename: f.name, data_base64: b64 });
      toast.success("Gutschein hochgeladen");
      loadContract();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeContract = async () => {
    if (!window.confirm("Gutschein wirklich entfernen?")) return;
    try {
      await api.delete(`/clients/${client.client_id}/contract`);
      toast.success("Gutschein entfernt");
      loadContract();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadContract = async () => {
    try {
      const res = await api.get(`/clients/${client.client_id}/contract`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = contract?.filename || "avgs-gutschein.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/clients/${client.client_id}`, { internal_notes: notesDraft || " " });
      toast.success("Notizen gespeichert");
      setEditingNotes(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingNotes(false); }
  };

  const reinvite = async () => {
    setReinviting(true);
    try {
      const { data } = await api.post(`/clients/${client.client_id}/reinvite`);
      try { await navigator.clipboard.writeText(data.invite_link); } catch { /* ignore */ }
      if (data.email_status === "sent") {
        toast.success(`Einladung an ${data.email_to} gesendet · Link auch kopiert`);
      } else if (data.email_status === "disabled") {
        toast.warning("E-Mail-Versand ist in dieser Umgebung nicht konfiguriert. Link wurde in die Zwischenablage kopiert — bitte manuell weiterleiten.", { duration: 10000 });
      } else {
        const detail = data.email_error ? `Resend meldet: ${data.email_error}` : "Resend-Fehler";
        toast.error(`E-Mail konnte nicht zugestellt werden (${detail}). Link wurde kopiert — bitte manuell weiterleiten.`, { duration: 15000 });
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setReinviting(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="client-akte-extras">
      {/* Kontakt */}
      <div className="bg-white border border-slate-200 p-5" data-testid="contact-card">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3 flex items-center gap-1">
          <Mail size={11} strokeWidth={1.5} /> Kontakt
        </div>
        {client.email ? (
          <a
            href={`mailto:${client.email}`}
            data-testid="contact-email"
            className="flex items-center gap-2 text-sm text-slate-900 hover:text-blue-700 mb-2 group"
          >
            <Mail size={14} strokeWidth={1.5} className="text-slate-400 group-hover:text-blue-700 flex-shrink-0" />
            <span className="font-mono text-xs truncate">{client.email}</span>
          </a>
        ) : (
          <div className="text-xs text-slate-400 italic mb-2">Keine E-Mail hinterlegt</div>
        )}
        {client.phone ? (
          <a
            href={`tel:${client.phone.replace(/\s+/g, "")}`}
            data-testid="contact-phone"
            className="flex items-center gap-2 text-sm text-slate-900 hover:text-blue-700 mb-2 group"
          >
            <Phone size={14} strokeWidth={1.5} className="text-slate-400 group-hover:text-blue-700 flex-shrink-0" />
            <span className="font-mono text-xs">{client.phone}</span>
          </a>
        ) : (
          <div className="text-xs text-slate-400 italic mb-2">Keine Telefonnummer hinterlegt</div>
        )}
        {(client.street || client.postal_code || client.city) ? (
          <div className="text-xs text-slate-700 mt-2 pt-2 border-t border-slate-100 leading-relaxed" data-testid="contact-address">
            {client.street && <div>{client.street}</div>}
            {(client.postal_code || client.city) && <div>{client.postal_code} {client.city}</div>}
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic mt-2 pt-2 border-t border-slate-100">Keine Adresse hinterlegt</div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
          {client.email && (
            <a
              href={`mailto:${client.email}?subject=${encodeURIComponent('AVGS-Coaching bei We Academy')}`}
              data-testid="contact-write-email"
              className="text-xs bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 flex-1 text-center"
            >
              E-Mail schreiben
            </a>
          )}
          {client.phone && (
            <a
              href={`tel:${client.phone.replace(/\s+/g, "")}`}
              className="text-xs border border-slate-300 text-slate-700 px-3 py-2 hover:bg-slate-100"
              title="Anrufen"
            >
              Anrufen
            </a>
          )}
        </div>
        {/* Portal-Status + Einladung erneut senden */}
        {client.portal_active === false && client.email && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="portal-invite-block">
            <div className="text-[10px] font-bold uppercase tracking-overline text-amber-700 mb-1">
              Portal-Konto noch nicht aktiviert
            </div>
            <div className="text-[11px] text-slate-500 mb-2 leading-relaxed">
              Der Kunde hat den Einladungslink noch nicht angenommen und kann sich noch nicht einloggen.
            </div>
            <button
              data-testid="client-reinvite-btn"
              onClick={reinvite}
              disabled={reinviting}
              className="text-xs bg-[#0a788c] text-white px-3 py-2 hover:bg-[#0a96b4] disabled:opacity-50 w-full"
            >
              {reinviting ? "Sende…" : "Einladung erneut senden"}
            </button>
          </div>
        )}
        {client.portal_active === true && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] font-bold uppercase tracking-overline text-emerald-700" data-testid="portal-active-indicator">
            Portal-Konto aktiv ✓
          </div>
        )}
      </div>

      {/* AVGS Gutschein */}
      <div className="bg-white border border-slate-200 p-5" data-testid="contract-card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">AVGS-Gutschein</div>
            <div className="font-heading text-lg font-light tracking-tighter text-slate-900 mt-1">Originaldokument</div>
          </div>
          <label className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100 flex items-center gap-1 cursor-pointer">
            <Upload size={12} strokeWidth={1.5} /> {contract?.exists ? "Ersetzen" : "Hochladen"}
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} disabled={uploading} data-testid="contract-upload-input" />
          </label>
        </div>
        {contract?.exists ? (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <FileText size={20} strokeWidth={1.5} className="text-slate-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 truncate" data-testid="contract-filename">{contract.filename}</div>
              <div className="text-[10px] text-slate-500 font-mono">
                {(contract.size / 1024).toFixed(0)} KB · {formatDeShort(contract.uploaded_at)}
              </div>
            </div>
            <button data-testid="contract-download" onClick={downloadContract} className="text-xs border border-slate-300 px-2 py-1 hover:bg-slate-100">
              <Download size={11} strokeWidth={1.5} />
            </button>
            <button data-testid="contract-delete" onClick={removeContract} className="text-xs border border-red-300 text-red-600 px-2 py-1 hover:bg-red-50">
              <Trash2 size={11} strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div className="text-xs text-slate-500 pt-2">
            Kein Gutschein hochgeladen. Lade die PDF hoch, damit du sie bei einer Prüfung sofort zur Hand hast.
          </div>
        )}
      </div>

      {/* Interne Notizen */}
      <div className="bg-white border border-slate-200 p-5" data-testid="internal-notes-card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1">
              <Lock size={11} strokeWidth={1.5} /> Interne Notizen
            </div>
            <div className="font-heading text-lg font-light tracking-tighter text-slate-900 mt-1">Nur Coach & Admin</div>
          </div>
          {!editingNotes && (
            <button
              data-testid="edit-notes-btn"
              onClick={() => { setNotesDraft(client.internal_notes || ""); setEditingNotes(true); }}
              className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100"
            >
              Bearbeiten
            </button>
          )}
        </div>
        {editingNotes ? (
          <>
            <textarea
              data-testid="internal-notes-textarea"
              className="input-tech min-h-[100px] text-xs font-mono w-full"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="z.B. Zielsetzung besprochen · Kunde bevorzugt Vormittag · nächster Schritt: Bewerbung Firma X..."
            />
            <div className="flex gap-2 mt-2">
              <button data-testid="save-notes-btn" onClick={saveNotes} disabled={savingNotes} className="text-xs bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1">
                <Save size={11} strokeWidth={1.5} /> {savingNotes ? "Speichert…" : "Speichern"}
              </button>
              <button onClick={() => setEditingNotes(false)} className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100">
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <div className="text-xs text-slate-700 whitespace-pre-wrap font-mono min-h-[40px]" data-testid="internal-notes-display">
            {(client.internal_notes || "").trim() || <span className="text-slate-400 italic">Noch keine Notizen. Klick auf &bdquo;Bearbeiten&ldquo; um welche hinzuzufügen.</span>}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_INFO = {
  proposed: { l: "Vorgeschlagen", c: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { l: "Angenommen", c: "bg-blue-50 text-blue-800 border-blue-200" },
  rejected: { l: "Abgelehnt", c: "bg-red-50 text-red-800 border-red-200" },
  completed: { l: "Stattgefunden · wartet auf Kunde", c: "bg-slate-100 text-slate-800 border-slate-300" },
  signed: { l: "Unterschrieben ✓", c: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

function TermineTab({ appointments, client, onDownload, onChanged }) {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || (user?.all_roles || []).includes("manager") || (user?.all_roles || []).includes("admin");
  const [showAll, setShowAll] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingSectionId, setSavingSectionId] = useState(null);
  const sorted = useMemo(
    () => [...appointments].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0)),
    [appointments]
  );
  const rows = showAll ? sorted : sorted.slice(0, 20);
  const sections = client.sections || [];
  const sectionOptions = useMemo(() => {
    const opts = [];
    // Onboarding/Offboarding sind IMMER wählbar (unabhängig davon, ob schon eine UE-Zielgröße
    // zugewiesen wurde) — das Backend erlaubt section_id="onboarding"/"offboarding" ohnehin
    // immer (siehe PUT /appointments/{id}/section).
    opts.push({ section_id: "onboarding", name: "Onboarding" });
    for (const s of sections) opts.push({ section_id: s.section_id, name: s.module_name ? `${s.module_name} · ${s.name}` : s.name });
    opts.push({ section_id: "offboarding", name: "Offboarding" });
    return opts;
  }, [client.onboarding_ue, client.offboarding_ue, sections]);

  const resendReminder = async (a) => {
    setSendingId(a.appointment_id);
    try {
      const r = await api.post(`/appointments/${a.appointment_id}/resend-signature-request`);
      toast.success(`Erinnerung an ${r.data.sent_to} gesendet`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSendingId(null); }
  };

  const copySignLink = async (a) => {
    try {
      const { data } = await api.get(`/appointments/${a.appointment_id}/signature-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Unterschrift-Link kopiert — jetzt an den Kunden schicken", { duration: 7000 });
      } catch {
        window.prompt("Unterschrift-Link kopieren:", data.url);
      }
      const txt = encodeURIComponent(`Hallo ${data.client_name || ""}, bitte den Termin am ${data.date} (${data.time_from}–${data.time_to}) hier unterschreiben: ${data.url}`);
      window.open(`https://wa.me/?text=${txt}`, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const assignSection = async (appointmentId, sectionId) => {
    setSavingSectionId(appointmentId);
    try {
      await api.put(`/appointments/${appointmentId}/section`, { section_id: sectionId || null });
      toast.success("Abschnitt zugeordnet — UE-Fortschritt aktualisiert");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingSectionId(null); }
  };

  const removeAppointment = async (a) => {
    if (!window.confirm(`Termin am ${formatDeShort(a.date)} löschen?`)) return;
    try {
      await api.delete(`/appointments/${a.appointment_id}`);
      toast.success("Termin gelöscht");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-heading text-2xl font-light tracking-tighter text-slate-900">
          Alle Termine für {client.name}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="open-appointment-dialog-for-client"
            className="text-xs bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 flex items-center gap-1"
          >
            <Plus size={12} strokeWidth={1.5} /> Termin vorschlagen
          </button>
          <button
            data-testid="download-akte-pdf"
            onClick={onDownload}
            className="text-xs border border-slate-300 px-3 py-2 hover:bg-slate-100 flex items-center gap-1"
          >
            <Download size={12} strokeWidth={1.5} /> Anwesenheitsliste PDF
          </button>
        </div>
      </div>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        clients={[client]}
        defaultClientId={client.client_id}
        initialData={editing}
        onSaved={() => { setDialogOpen(false); setEditing(null); onChanged && onChanged(); }}
      />

      {sorted.length === 0 && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <CalendarClock size={24} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Noch keine Termine. Coaches können unter &bdquo;Termine&ldquo; neue Termine vorschlagen.</p>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((a) => {
          const s = STATUS_INFO[a.status] || STATUS_INFO.proposed;
          return (
            <div key={a.appointment_id} className="bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`akte-appt-${a.appointment_id}`}>
              <div className="flex-shrink-0 w-14 text-center border-r border-slate-100 pr-3">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{a.date.slice(5, 7)}/{a.date.slice(2, 4)}</div>
                <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{a.date.slice(8, 10)}</div>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-medium text-slate-900">{a.topic || "Coaching-Termin"}</div>
                <div className="text-xs text-slate-600 font-mono mt-1 flex items-center gap-3 flex-wrap">
                  {(a.time_from || a.time_to) && <span>{a.time_from || "?"}–{a.time_to || "?"}</span>}
                  <span>{a.planned_ue} UE</span>
                  <span>· {a.mode === "online" ? "Remote" : "Präsenz"}</span>
                  <span className="text-slate-400">mit {a.coach_name}</span>
                </div>
                {a.client_signed_at && (
                  <div className="text-[10px] text-emerald-700 mt-1" data-testid={`akte-appt-signed-at-${a.appointment_id}`}>
                    Unterschrieben am {formatDeShortDateTime(a.client_signed_at)}
                  </div>
                )}
                {a.notes && <div className="text-xs text-slate-500 italic mt-1 truncate max-w-[500px]">{a.notes}</div>}
                {a.last_signature_reminder_at && a.status === "completed" && (
                  <div className="text-[10px] text-amber-700 mt-1">
                    Erinnerung zuletzt: {formatDeShortDateTime(a.last_signature_reminder_at)}
                  </div>
                )}
                {a.edited_after_signature_by_name && (a.status === "completed" || a.status === "signed") && (
                  <div className="text-[10px] text-amber-700 mt-1" data-testid={`akte-appt-edited-hint-${a.appointment_id}`}>
                    Nachträglich geändert von {a.edited_after_signature_by_name}
                    {a.edited_after_signature_at && ` am ${formatDeShortDateTime(a.edited_after_signature_at)}`}
                  </div>
                )}
                {sectionOptions.length > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <select
                      data-testid={`akte-appt-section-${a.appointment_id}`}
                      className="text-[11px] border border-slate-300 px-2 py-1 bg-white disabled:opacity-50"
                      value={a.section_id || ""}
                      disabled={savingSectionId === a.appointment_id}
                      onChange={(e) => assignSection(a.appointment_id, e.target.value)}
                    >
                      <option value="">— kein Abschnitt zugeordnet —</option>
                      {sectionOptions.map((s) => (
                        <option key={s.section_id} value={s.section_id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    {!a.section_id && (
                      <span className="text-[10px] text-amber-700">UE zählt in keinem Abschnitt-Fortschritt</span>
                    )}
                  </div>
                )}
              </div>
              <div className={`px-3 py-1 text-xs font-medium border ${s.c}`}>{s.l}</div>
              {a.status === "completed" && (
                <button
                  data-testid={`resend-sig-${a.appointment_id}`}
                  onClick={() => resendReminder(a)}
                  disabled={sendingId === a.appointment_id}
                  className="px-3 py-2 text-xs bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
                  title="Kunde per E-Mail an Unterschrift erinnern"
                >
                  <Mail size={12} strokeWidth={1.5} />
                  {sendingId === a.appointment_id ? "Sende…" : "Erinnerung"}
                </button>
              )}
              {a.status === "completed" && (
                <button
                  data-testid={`akte-sign-link-${a.appointment_id}`}
                  onClick={() => copySignLink(a)}
                  className="px-3 py-2 text-xs border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                  title="Unterschrift-Link kopieren – zum Weitergeben per WhatsApp oder SMS"
                >
                  <Link2 size={12} strokeWidth={1.5} /> Unterschrift-Link
                </button>
              )}
              {(isPrivileged || (a.created_by || a.coach_user_id) === user?.user_id) && (
                <div className="flex gap-1">
                  <button
                    data-testid={`akte-appt-edit-${a.appointment_id}`}
                    onClick={() => { setEditing(a); setDialogOpen(true); }}
                    className="p-2 border border-slate-200 text-slate-600 hover:bg-slate-100"
                    aria-label="Bearbeiten"
                    title="UE/Zeit nachträglich ändern"
                  ><Pencil size={12} strokeWidth={1.5} /></button>
                  <button
                    data-testid={`akte-appt-delete-${a.appointment_id}`}
                    onClick={() => removeAppointment(a)}
                    className="p-2 border border-slate-200 text-red-600 hover:bg-red-50"
                    aria-label="Löschen"
                    title="Termin löschen"
                  ><Trash2 size={12} strokeWidth={1.5} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sorted.length > 20 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
        >
          {showAll ? "Weniger anzeigen" : `${sorted.length - 20} weitere anzeigen`}
        </button>
      )}
    </div>
  );
}
